import { useEffect, useState } from "react";
import { AdminApi, type PlatformAdmin } from "@/api/adminApi";
import { RestClient } from "@/api/restClient";

const apiUrl = import.meta.env.VITE_API_URL || "";
const client = new RestClient({ apiUrl });
const adminApi = new AdminApi(client);

export function AdminPlatformAdmins() {
  const [admins, setAdmins] = useState<PlatformAdmin[]>([]);
  const [loading, setLoading] = useState(true);
  const [addEmail, setAddEmail] = useState("");
  const [adding, setAdding] = useState(false);

  const loadAdmins = async () => {
    setLoading(true);
    try {
      const res = await adminApi.listPlatformAdmins();
      setAdmins(res.admins);
    } catch {
      // error
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadAdmins(); }, []);

  const handleAdd = async () => {
    if (!addEmail.trim()) return;
    setAdding(true);
    try {
      await adminApi.addPlatformAdmin(undefined, addEmail.trim());
      setAddEmail("");
      loadAdmins();
    } catch (e: any) {
      alert(e.message || "Failed to add admin");
    } finally {
      setAdding(false);
    }
  };

  const handleRemove = async (userId: string) => {
    if (!confirm("Remove this platform admin?")) return;
    try {
      await adminApi.removePlatformAdmin(userId);
      loadAdmins();
    } catch (e: any) {
      alert(e.message || "Failed to remove admin");
    }
  };

  return (
    <div style={{ maxWidth: 600 }}>
      <h1 style={{ fontSize: 24, fontWeight: 600, marginBottom: 16 }}>Platform Admins</h1>
      <p style={{ fontSize: 13, color: "var(--text-secondary, #666)", marginBottom: 24 }}>
        Platform admins have unrestricted access to all organizations, users, and billing.
        This is a highly privileged role — grant it carefully.
      </p>

      {/* Add admin */}
      <div style={{ display: "flex", gap: 8, marginBottom: 24 }}>
        <input
          type="email"
          placeholder="Email address..."
          value={addEmail}
          onChange={(e) => setAddEmail(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && handleAdd()}
          style={{
            flex: 1,
            padding: "8px 12px",
            border: "1px solid var(--border-color, #e0e0e0)",
            borderRadius: 6,
            fontSize: 14,
          }}
        />
        <button
          onClick={handleAdd}
          disabled={adding || !addEmail.trim()}
          style={{
            padding: "8px 16px",
            background: "var(--primary, #2563eb)",
            color: "#fff",
            border: "none",
            borderRadius: 6,
            cursor: "pointer",
            fontSize: 14,
            opacity: adding ? 0.6 : 1,
          }}
        >
          {adding ? "Adding..." : "Add Admin"}
        </button>
      </div>

      {/* List */}
      {loading ? (
        <p>Loading...</p>
      ) : admins.length === 0 ? (
        <p style={{ color: "var(--text-secondary, #666)" }}>No platform admins found.</p>
      ) : (
        <div>
          {admins.map((a) => (
            <div
              key={a.user_id}
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                padding: "12px 0",
                borderBottom: "1px solid var(--border-color, #f0f0f0)",
              }}
            >
              <div>
                <div style={{ fontWeight: 500, fontSize: 14 }}>{a.email || "Unknown"}</div>
                <div style={{ fontSize: 11, color: "var(--text-secondary, #666)" }}>{a.user_id}</div>
              </div>
              <button
                onClick={() => handleRemove(a.user_id)}
                style={{
                  fontSize: 12,
                  padding: "4px 12px",
                  cursor: "pointer",
                  color: "red",
                  border: "1px solid red",
                  borderRadius: 4,
                  background: "transparent",
                }}
              >
                Remove
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
